import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { TENANT_EXPORT_CONFIGURATION_QUERY } from '../../graphql/adminConfig'
import {
  START_TENANT_DATA_EXPORT_V2_MUTATION,
  TENANT_DATA_EXPORT_JOB_QUERY,
  TENANT_DATA_EXPORT_JOBS_QUERY
} from '../../graphql/tenant'
import { getTimeZone } from '../../helpers/userData'
import { lang } from '../../localization'
import { getTranslate } from '../../localization'
import { downloadTenantDataExportArtifact } from './download'
import useStyles from './styles'

type DomainValue =
  | 'tenant'
  | 'accommodations'
  | 'booking_policies'
  | 'contracts'
  | 'channel_references'
  | 'subscriptions'
  | 'bookings'
  | 'payments'
  | 'pms'
  | 'media'
  | 'reference_data'

type Domain = {
  value: DomainValue
  label: string
  description: string
}

const domainGroups: {
  title: string
  description: string
  domains: Domain[]
}[] = [
  {
    title: 'Tenant Profile',
    description:
      'Tenant identity, related properties, media, and reference data.',
    domains: [
      {
        value: 'tenant',
        label: 'Tenant',
        description: 'Tenant account and profile records.'
      },
      {
        value: 'accommodations',
        label: 'Accommodations',
        description: 'Accommodation records connected to the tenant.'
      },
      {
        value: 'media',
        label: 'Media',
        description: 'Images and media references for tenant accommodations.'
      },
      {
        value: 'reference_data',
        label: 'Reference Data',
        description: 'Tenant-specific supporting lookup records.'
      }
    ]
  },
  {
    title: 'Commercial Setup',
    description: 'Contract configuration and sellable product setup.',
    domains: [
      {
        value: 'booking_policies',
        label: 'Booking Policies',
        description: 'Cancellation, amendment, and booking policy records.'
      },
      {
        value: 'contracts',
        label: 'Contracts',
        description: 'Contract patterns, objects, and contract inventory.'
      },
      {
        value: 'subscriptions',
        label: 'Subscriptions',
        description: 'Tenant subscriptions and commercial relationships.'
      }
    ]
  },
  {
    title: 'Transactions',
    description: 'Operational records created from sales and payments.',
    domains: [
      {
        value: 'bookings',
        label: 'Bookings',
        description: 'Reservations and tenant booking records.'
      },
      {
        value: 'payments',
        label: 'Payments',
        description: 'Payment records related to exported bookings.'
      }
    ]
  },
  {
    title: 'Integrations',
    description: 'Tenant-specific integration references.',
    domains: [
      {
        value: 'channel_references',
        label: 'Channel References',
        description: 'Channel identifiers and names referenced by tenant data.'
      },
      {
        value: 'pms',
        label: 'PMS',
        description: 'PMS mappings and tenant-specific integration records.'
      }
    ]
  }
]

const domains = domainGroups.flatMap((group) => group.domains)
const domainMap = new Map(domains.map((domain) => [domain.value, domain]))
const orderedDomainValues = domains.map((domain) => domain.value)

const domainDependencies: Partial<Record<DomainValue, DomainValue[]>> = {
  accommodations: ['tenant'],
  media: ['accommodations'],
  reference_data: ['tenant'],
  booking_policies: ['accommodations'],
  contracts: ['accommodations', 'booking_policies', 'channel_references'],
  subscriptions: ['contracts'],
  bookings: ['accommodations', 'contracts', 'channel_references'],
  payments: ['bookings'],
  channel_references: ['tenant'],
  pms: ['accommodations']
}

const sortDomains = (values: Iterable<DomainValue>) =>
  orderedDomainValues.filter((domain) => new Set(values).has(domain))

const collectDependencies = (
  domain: DomainValue,
  collected = new Set<DomainValue>()
) => {
  domainDependencies[domain]?.forEach((dependency) => {
    if (collected.has(dependency)) return
    collected.add(dependency)
    collectDependencies(dependency, collected)
  })
  return collected
}

const collectDependents = (
  domain: DomainValue,
  selected: Set<DomainValue>,
  collected = new Set<DomainValue>()
) => {
  selected.forEach((selectedDomain) => {
    if (collected.has(selectedDomain)) return
    const dependencies = collectDependencies(selectedDomain)
    if (dependencies.has(domain)) {
      collected.add(selectedDomain)
      collectDependents(selectedDomain, selected, collected)
    }
  })
  return collected
}

type ExportJob = {
  profile?: 'FULL' | 'BUSINESS'
  contractVersion?: number
  schemaVersion?: number
  includePersonalData?: boolean
  artifactId: string
  status: string
  error?: string | null
  failureCode?: string | null
  artifactSize?: number | string
  expiresAt?: string | null
  datasets: { domain: string; dataset: string; rowCount: number }[]
}

export const exportFailureMessage = (job: ExportJob): string => {
  if (
    ['ROW_LIMIT_EXCEEDED', 'EXPORT_ROW_LIMIT_EXCEEDED'].includes(
      job.failureCode || ''
    )
  ) {
    return getTranslate(
      'Export exceeds the configured row limit. Select less data or ask an administrator to raise the limit.'
    )
  }
  if (
    ['ARTIFACT_SIZE_EXCEEDED', 'EXPORT_FILE_SIZE_LIMIT_EXCEEDED'].includes(
      job.failureCode || ''
    )
  ) {
    return getTranslate(
      'Export exceeds the configured artifact size limit. Select less data or ask an administrator to raise the limit.'
    )
  }
  return getTranslate('Export failed')
}

const TenantDataExportView = () => {
  const classes = useStyles()
  const { tenantId } = useParams<{ tenantId: string }>()
  const [format, setFormat] = useState<'CSV' | 'SQL'>('CSV')
  const [profile, setProfile] = useState<'BUSINESS' | 'FULL'>('BUSINESS')
  const [includePersonalData, setIncludePersonalData] = useState(false)
  const [selectedDomains, setSelectedDomains] = useState<DomainValue[]>(
    domains.map((domain) => domain.value)
  )
  const [lastExport, setLastExport] = useState<ExportJob | null>(null)
  const historyInitialized = useRef(false)
  const acceptedJob = useRef<ExportJob | null>(null)
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const [exportJobs, setExportJobs] = useState<ExportJob[]>([])
  const [expandedArtifactId, setExpandedArtifactId] = useState<string | null>(
    null
  )
  const [downloadingArtifactId, setDownloadingArtifactId] = useState<
    string | null
  >(null)

  const [startExport, { loading: starting }] = useMutation(
    START_TENANT_DATA_EXPORT_V2_MUTATION
  )
  const [fetchExportJob] = useLazyQuery(TENANT_DATA_EXPORT_JOB_QUERY, {
    fetchPolicy: 'network-only'
  })
  const { data: exportJobsData, refetch: refetchExportJobs } = useQuery(
    TENANT_DATA_EXPORT_JOBS_QUERY,
    {
      variables: { tenantId, limit: 10 },
      fetchPolicy: 'network-only',
      skip: !tenantId
    }
  )
  const { data: exportConfigurationData } = useQuery(
    TENANT_EXPORT_CONFIGURATION_QUERY,
    { fetchPolicy: 'cache-first' }
  )

  const selectedCount = useMemo(() => selectedDomains.length, [selectedDomains])

  useEffect(() => {
    let jobs = exportJobsData?.tenantDataExportJobs || []
    const accepted = acceptedJob.current
    if (accepted) {
      if (jobs.some((job: ExportJob) => job.artifactId === accepted.artifactId))
        acceptedJob.current = null
      else jobs = [accepted, ...jobs].slice(0, 10)
    }
    setExportJobs(jobs)
    if (!historyInitialized.current && jobs.length > 0) {
      historyInitialized.current = true
      setLastExport((current) => current || jobs[0])
      setExpandedArtifactId((current) => current || jobs[0].artifactId)
    }
  }, [exportJobsData])

  const refreshExportHistory = useCallback(async () => {
    try {
      await refetchExportJobs()
    } catch {
      if (!active.current) return
      toast.error(
        getTranslate('Export history could not be refreshed. Reload to retry.')
      )
    }
  }, [refetchExportJobs])

  const toggleDomain = (domain: DomainValue) => {
    setSelectedDomains((current) => {
      const selected = new Set(current)

      if (selected.has(domain)) {
        selected.delete(domain)
        collectDependents(domain, selected).forEach((dependent) =>
          selected.delete(dependent)
        )
      } else {
        selected.add(domain)
        collectDependencies(domain).forEach((dependency) =>
          selected.add(dependency)
        )
      }

      return sortDomains(selected)
    })
  }

  const selectAllDomains = () => {
    setSelectedDomains(domains.map((domain) => domain.value))
  }

  const clearDomains = () => {
    setSelectedDomains([])
  }

  const handleStart = async () => {
    if (profile === 'FULL' && !selectedDomains.length) {
      toast.error(getTranslate('Select at least one domain'))
      return
    }

    try {
      const response = await startExport({
        variables: {
          input: {
            tenantId,
            expectedContractVersion: profile === 'BUSINESS' ? 3 : 2,
            profile,
            ...(profile === 'BUSINESS'
              ? { businessOptions: { includePersonalData } }
              : { domains: selectedDomains }),
            format
          }
        }
      })

      const exportData = response.data?.startTenantDataExportV2
      if (!active.current) return
      if (!exportData?.artifactId) {
        throw new Error('Missing export acceptance')
      } else {
        setLastExport(exportData)
        acceptedJob.current = exportData
        setExpandedArtifactId(exportData.artifactId)
        setExportJobs((current) => [exportData, ...current])
        toast.success(getTranslate('Export started'))
        await refreshExportHistory()
      }
    } catch {
      if (!active.current) return
      toast.error(
        getTranslate(
          'Export could not start. Check service availability and export options.'
        )
      )
    }
  }

  const handleDownload = async (artifactId: string) => {
    setDownloadingArtifactId(artifactId)
    try {
      await downloadTenantDataExportArtifact(artifactId)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : getTranslate('Download failed')
      )
    } finally {
      setDownloadingArtifactId(null)
    }
  }

  useEffect(() => {
    if (
      !lastExport?.artifactId ||
      lastExport.status === 'completed' ||
      lastExport.status === 'failed'
    ) {
      return undefined
    }

    let inFlight = false
    let stopped = false
    let failures = 0
    let timer: number | undefined
    const refresh = async () => {
      if (inFlight || stopped) return
      inFlight = true
      try {
        const response = await fetchExportJob({
          variables: { artifactId: lastExport.artifactId }
        })
        const job = response.data?.tenantDataExportJob
        if (stopped) return
        if (!job) throw new Error('Missing export status')
        failures = 0
        if (acceptedJob.current?.artifactId === job.artifactId)
          acceptedJob.current = job

        setLastExport(job)
        setExportJobs((current) =>
          current.map((item) =>
            item.artifactId === job.artifactId ? { ...item, ...job } : item
          )
        )
        if (job.status === 'completed') {
          toast.success(getTranslate('Export is ready'))
          await refreshExportHistory()
        }
        if (job.status === 'failed') {
          toast.error(exportFailureMessage(job))
          await refreshExportHistory()
        }
      } catch {
        if (stopped) return
        failures++
        if (failures === 1)
          toast.error(
            getTranslate(
              'Export status could not be refreshed. Reload to retry.'
            )
          )
        if (failures >= 3) window.clearInterval(timer)
      } finally {
        inFlight = false
      }
    }

    refresh()
    timer = window.setInterval(refresh, 3000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [
    fetchExportJob,
    lastExport?.artifactId,
    lastExport?.status,
    refreshExportHistory
  ])

  const userTimeZone =
    getTimeZone() || Intl.DateTimeFormat().resolvedOptions().timeZone
  const formatDateTime = (value?: string | null) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat(lang === 'fa' ? 'fa-IR' : 'en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: userTimeZone
    }).format(date)
  }

  const formatSize = (value?: number | string) => {
    const bytes = typeof value === 'string' ? Number(value) : value
    if (!bytes || !Number.isFinite(bytes)) return '-'
    return new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-US', {
      style: 'unit',
      unit: 'megabyte',
      maximumFractionDigits: 1
    }).format(bytes / 1024 / 1024)
  }

  const isDownloadable = (job: ExportJob) => {
    if (job.status !== 'completed') return false
    if (!job.expiresAt) return true
    return new Date(job.expiresAt).getTime() > Date.now()
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h6" className={classes.title}>
            {getTranslate('Tenant Data Export')}
          </Typography>
          <Typography className={classes.description}>
            {getTranslate(
              'Export tenant-owned operational, commercial, and integration records into a downloadable artifact.'
            )}
          </Typography>
        </div>
      </div>

      <div className={classes.toolbar}>
        <div className={classes.controls}>
          <FormControl variant="outlined" size="small">
            <InputLabel id="export-scope-label">
              {getTranslate('Export scope')}
            </InputLabel>
            <Select
              labelId="export-scope-label"
              value={profile}
              disabled={starting}
              onChange={(event) => {
                setProfile(event.target.value as 'BUSINESS' | 'FULL')
                setIncludePersonalData(false)
              }}
              label={getTranslate('Export scope')}>
              <MenuItem value="BUSINESS">
                {getTranslate('Business data')}
              </MenuItem>
              <MenuItem value="FULL">{getTranslate('Full data')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small">
            <InputLabel id="export-format-label">
              {getTranslate('Format')}
            </InputLabel>
            <Select
              labelId="export-format-label"
              value={format}
              disabled={starting}
              onChange={(event) =>
                setFormat(event.target.value as 'CSV' | 'SQL')
              }
              label={getTranslate('Format')}>
              <MenuItem value="CSV">CSV</MenuItem>
              <MenuItem value="SQL">SQL</MenuItem>
            </Select>
          </FormControl>
          {profile === 'FULL' && (
            <Typography className={classes.muted}>
              {getTranslate('{count} domains selected', {
                count: selectedCount
              })}
            </Typography>
          )}
          {exportConfigurationData?.tenantExportConfiguration && (
            <Chip
              size="small"
              variant="outlined"
              label={getTranslate('Standard policy: {rows} rows / {size} MB', {
                rows: exportConfigurationData.tenantExportConfiguration.maxRows.toLocaleString(),
                size: exportConfigurationData.tenantExportConfiguration
                  .maxArtifactSizeMegabytes
              })}
            />
          )}
        </div>
        {profile === 'FULL' && (
          <div className={classes.quickActions}>
            <Button size="small" onClick={selectAllDomains} disabled={starting}>
              {getTranslate('Select All')}
            </Button>
            <Button size="small" onClick={clearDomains} disabled={starting}>
              {getTranslate('Clear')}
            </Button>
          </div>
        )}
      </div>

      <section className={classes.domainGroup}>
        <Typography className={classes.groupDescription}>
          {getTranslate(
            profile === 'BUSINESS'
              ? 'Properties, facilities, rooms, rate plans, booking snapshots, financials and payments. Excludes subscriptions, history, inventory and private notes.'
              : 'Full data contains comprehensive redacted records and may include personal data. Select the domains below.'
          )}
        </Typography>
        {profile === 'BUSINESS' && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={includePersonalData}
                  disabled={starting}
                  onChange={(event) =>
                    setIncludePersonalData(event.target.checked)
                  }
                />
              }
              label={getTranslate('Include guest and booker personal data')}
            />
            <Typography className={classes.muted}>
              {getTranslate(
                'Off by default. Includes names, contact details and identification data. Share only with authorized recipients.'
              )}
            </Typography>
          </>
        )}
        <Typography className={classes.muted}>
          {getTranslate(
            'All normalized rows count toward the row limit. Splitting files does not increase the total export limit.'
          )}
        </Typography>
        {format === 'SQL' && (
          <Typography className={classes.muted}>
            {getTranslate(
              'SQL uses a documented ISO SQL core subset, not a vendor-native dump. Some databases require conversion. Schema and import instructions are included.'
            )}
          </Typography>
        )}
      </section>

      {profile === 'FULL' && (
        <div className={classes.domainGroups}>
          {domainGroups.map((group) => (
            <section key={group.title} className={classes.domainGroup}>
              <div className={classes.groupHeader}>
                <Typography variant="subtitle1" className={classes.groupTitle}>
                  {getTranslate(group.title)}
                </Typography>
                <Typography className={classes.groupDescription}>
                  {getTranslate(group.description)}
                </Typography>
              </div>
              <FormGroup className={classes.domains}>
                {group.domains.map((domain) => {
                  const dependencies = sortDomains(
                    collectDependencies(domain.value)
                  )

                  return (
                    <div key={domain.value} className={classes.domainRow}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            disabled={starting}
                            checked={selectedDomains.includes(domain.value)}
                            onChange={() => toggleDomain(domain.value)}
                          />
                        }
                        label={
                          <div>
                            <Typography className={classes.domainLabel}>
                              {getTranslate(domain.label)}
                            </Typography>
                            <Typography className={classes.domainDescription}>
                              {getTranslate(domain.description)}
                            </Typography>
                          </div>
                        }
                      />
                      {dependencies.length > 0 && (
                        <Typography className={classes.dependencyText}>
                          {getTranslate('Requires')}:&nbsp;
                          {dependencies
                            .map((dependency) =>
                              getTranslate(
                                domainMap.get(dependency)?.label || ''
                              )
                            )
                            .join(', ')}
                        </Typography>
                      )}
                    </div>
                  )
                })}
              </FormGroup>
            </section>
          ))}
        </div>
      )}

      <div className={classes.actions}>
        <Button
          color="primary"
          variant="contained"
          disabled={starting || (profile === 'FULL' && !selectedDomains.length)}
          onClick={handleStart}>
          {getTranslate('Start Export')}
        </Button>
      </div>

      <div className={classes.history}>
        <Typography variant="subtitle1" className={classes.historyTitle}>
          {getTranslate('Export history')}
        </Typography>
        {exportJobs.length === 0 ? (
          <Typography className={classes.muted}>
            {getTranslate('No export history')}
          </Typography>
        ) : (
          exportJobs.map((job) => {
            const expanded = expandedArtifactId === job.artifactId
            return (
              <div key={job.artifactId} className={classes.historyItem}>
                <div
                  role="button"
                  aria-expanded={expanded}
                  tabIndex={0}
                  className={classes.historyRow}
                  onClick={() => {
                    setLastExport(job)
                    setExpandedArtifactId(expanded ? null : job.artifactId)
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    setLastExport(job)
                    setExpandedArtifactId(expanded ? null : job.artifactId)
                  }}>
                  <span>
                    <Typography className={classes.historyStatus}>
                      {getTranslate('Export job')}
                    </Typography>
                    <Typography className={classes.muted}>
                      {job.artifactId}
                    </Typography>
                    <Typography className={classes.historyMeta}>
                      {getTranslate(
                        !job.profile
                          ? 'Legacy export — scope not recorded'
                          : job.profile === 'BUSINESS'
                          ? 'Business data'
                          : 'Full data'
                      )}
                      {' · '}
                      {getTranslate('Schema version')}: {job.schemaVersion || 1}
                      {job.profile === 'BUSINESS' &&
                        ` · ${getTranslate(
                          job.includePersonalData
                            ? 'Personal data included'
                            : 'Personal data excluded'
                        )}`}
                    </Typography>
                    <Typography className={classes.historyMeta}>
                      {getTranslate('Artifact size')}:&nbsp;
                      {formatSize(job.artifactSize)}
                      {job.expiresAt
                        ? ` · ${getTranslate('Expires at')}: ${formatDateTime(
                            job.expiresAt
                          )}`
                        : ''}
                    </Typography>
                  </span>
                  <span className={classes.historyActions}>
                    <Chip
                      size="small"
                      className={
                        job.status === 'completed'
                          ? classes.statusCompleted
                          : job.status === 'failed'
                          ? classes.statusFailed
                          : undefined
                      }
                      label={getTranslate(job.status)}
                    />
                    <Button
                      color="primary"
                      variant="outlined"
                      size="small"
                      startIcon={<GetAppIcon />}
                      disabled={
                        !isDownloadable(job) || downloadingArtifactId !== null
                      }
                      onClick={(event) => {
                        event.stopPropagation()
                        handleDownload(job.artifactId)
                      }}>
                      {downloadingArtifactId === job.artifactId
                        ? getTranslate('Downloading')
                        : getTranslate('Download')}
                    </Button>
                  </span>
                </div>
                {expanded && (
                  <div className={classes.datasetList}>
                    {(job.error || job.failureCode) && (
                      <Typography color="error">
                        {exportFailureMessage(job)}
                      </Typography>
                    )}
                    {job.datasets.map((dataset) => (
                      <Typography
                        key={`${dataset.domain}-${dataset.dataset}`}
                        color={
                          dataset.rowCount === 0 && job.profile !== 'BUSINESS'
                            ? 'error'
                            : undefined
                        }>
                        {dataset.domain} / {dataset.dataset} —{' '}
                        {dataset.rowCount.toLocaleString()}{' '}
                        {getTranslate('rows')}
                        {dataset.rowCount === 0 && job.profile !== 'BUSINESS'
                          ? ' ⚠'
                          : ''}
                      </Typography>
                    ))}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// TenantTabs reuses the route component across tenants. A keyed view makes
// history, polling, downloads and personal-data consent tenant-local state.
const TenantDataExport = () => {
  const { tenantId } = useParams<{ tenantId: string }>()
  return <TenantDataExportView key={tenantId} />
}

export default TenantDataExport
